import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpHeaders,
  HttpErrorResponse,
} from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, BehaviorSubject, throwError, of, timer } from 'rxjs';
import {
  map,
  catchError,
  tap,
  finalize,
  retry,
  delay,
  switchMap,
} from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  searchResults?: SearchResults;
  compareResults?: ComparisonResult[];
  error?: boolean;
  ragEnhanced?: boolean;
  ragListingsFound?: number;
}

export interface SearchResults {
  results: SearchListing[];
  total: number;
  category: string;
  query?: string;
  ragEnhanced?: boolean;
}

export interface SearchListing {
  id: number;
  title: string;
  price: number;
  currency: string;
  location: string;
  category: string;
  subcategory: string;
  first_image?: string;
  is_featured: boolean;
  created_at: string;
  brand?: string;
  model?: string;
  year?: number;
}

export interface ComparisonResult {
  id: number;
  title: string;
  price: number;
  currency: string;
  location: string;
  category: string;
  subcategory: string;
  year?: number;
  square_meters?: number;
  first_image?: string;
  brand?: string;
  model?: string;
  rooms?: number;
  [key: string]: any;
}

export interface ChatResponse {
  message: string;
  search_results?: SearchResults;
  compare_results?: ComparisonResult[];
  intent: 'chat' | 'search' | 'compare' | 'error';
  timestamp: string;
  error?: boolean;
  success?: boolean;
  rag_enhanced?: boolean;
  rag_listings_found?: number;
}

export interface HealthResponse {
  status: 'healthy' | 'unhealthy';
  ai_service: 'operational' | 'degraded' | 'unavailable';
  timestamp: string;
  version?: string;
  error?: string;
  features?: {
    naturalLanguageSearch: boolean;
    listingComparison: boolean;
    conversationalChat: boolean;
    streamingResponses: boolean;
    contextAwareResponses: boolean;
    perfectMemory: boolean;
    ragPoweredSearch: boolean;
    semanticSearch: boolean;
    featureDetails: boolean;
  };
  ai_service_details?: {
    hasApiKey: boolean;
    hasValidApiKey: boolean;
    lastHealthCheck: string;
    error: string;
    note?: string;
  };
}

export interface CompanyInfo {
  name: string;
  description: string;
  features: string[];
  categories: {
    [key: string]: {
      name: string;
      description: string;
      subcategories: string[];
    };
  };
  contact: {
    support: string;
    helpCenter: string;
    contactUs: string;
  };
  aiFeatures: {
    naturalLanguageSearch: string;
    listingComparison: string;
    helpAndGuidance: string;
    conversationalInterface: string;
  };
}

@Injectable({
  providedIn: 'root',
})
export class ChatService {
  private apiUrl = environment.apiUrl;

  private isThinkingSubject = new BehaviorSubject<boolean>(false);
  public isThinking$ = this.isThinkingSubject.asObservable();

  private isOnlineSubject = new BehaviorSubject<boolean>(true);
  public isOnline$ = this.isOnlineSubject.asObservable();

  private retryCount = 0;
  private maxRetries = 2;

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private router: Router
  ) {
    this.initializeHealthCheck();
  }

  private initializeHealthCheck(): void {
    timer(0, 5 * 60 * 1000).subscribe(() => {
      this.checkHealth().subscribe({
        next: (health) => {
          this.isOnlineSubject.next(
            health.features?.naturalLanguageSearch === true
          );
        },
        error: () => {
          this.isOnlineSubject.next(false);
        },
      });
    });
  }

  private getHeaders(): HttpHeaders {
    let headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    const token = this.authService.getToken();
    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }

    return headers;
  }

  isEnabled(): Observable<boolean> {
    return this.authService.isLoggedIn$.pipe(
      switchMap((isLoggedIn) => {
        if (!isLoggedIn) {
          return of(false);
        }
        return this.checkHealth().pipe(
          map((health) => health.features?.naturalLanguageSearch === true),
          catchError(() => {
            return of(false);
          })
        );
      })
    );
  }

  sendMessage(
    message: string,
    conversationHistory: Array<{ role: string; content: string }> = []
  ): Observable<ChatResponse> {
    if (!this.authService.isLoggedIn()) {
      this.redirectToLogin();
      return throwError(() => new Error('Authentication required'));
    }

    if (
      !message ||
      typeof message !== 'string' ||
      message.trim().length === 0
    ) {
      return throwError(() => new Error('Message cannot be empty'));
    }

    if (message.length > 1000) {
      return throwError(
        () => new Error('Message is too long (max 1000 characters)')
      );
    }

    this.isThinkingSubject.next(true);
    this.retryCount = 0;

    const payload = {
      message: message.trim(),
      conversation_history:
        this.validateConversationHistory(conversationHistory),
    };

    return this.http
      .post<ChatResponse>(`${this.apiUrl}/chat/message`, payload, {
        headers: this.getHeaders(),
      })
      .pipe(
        map((response) => this.validateChatResponse(response)),
        tap((response) => {
          this.trackChatInteraction(message, response.intent);
        }),
        catchError((error) =>
          this.handleChatError(error, message, conversationHistory)
        ),
        finalize(() => {
          this.isThinkingSubject.next(false);
        })
      );
  }

  private validateConversationHistory(
    history: Array<{ role: string; content: string }>
  ): Array<{ role: string; content: string }> {
    if (!Array.isArray(history)) {
      return [];
    }

    return history
      .slice(-15)
      .filter((msg) => {
        return (
          msg &&
          typeof msg === 'object' &&
          typeof msg.role === 'string' &&
          typeof msg.content === 'string' &&
          ['user', 'assistant'].includes(msg.role) &&
          msg.content.trim().length > 0
        );
      })
      .map((msg) => ({
        role: msg.role === 'user' ? 'user' : 'assistant',
        content: msg.content.trim(),
      }));
  }

  private validateChatResponse(response: any): ChatResponse {
    if (!response || typeof response !== 'object') {
      throw new Error('Invalid response format');
    }

    const validatedResponse: ChatResponse = {
      message: response.message || 'No response message received',
      intent: response.intent || 'chat',
      timestamp: response.timestamp || new Date().toISOString(),
      success: response.success !== false,
      error: response.error === true,
      rag_enhanced: response.rag_enhanced || false,
      rag_listings_found: response.rag_listings_found || 0,
    };

    if (response.search_results) {
      if (this.isValidSearchResults(response.search_results)) {
        validatedResponse.search_results = {
          ...response.search_results,
          ragEnhanced: response.search_results.ragEnhanced || false,
        };
      }
    }

    return validatedResponse;
  }

  private isValidSearchResults(searchResults: any): boolean {
    return (
      searchResults &&
      typeof searchResults === 'object' &&
      Array.isArray(searchResults.results) &&
      typeof searchResults.total === 'number' &&
      typeof searchResults.category === 'string'
    );
  }

  private handleChatError(
    error: HttpErrorResponse,
    originalMessage: string,
    conversationHistory: Array<{ role: string; content: string }>
  ): Observable<ChatResponse> {
    if (error.status === 401) {
      this.redirectToLogin();
      return throwError(
        () => new Error('Authentication required. Please log in.')
      );
    }

    if (error.status === 0) {
      this.isOnlineSubject.next(false);
    }

    if (error.status === 429) {
      return throwError(
        () =>
          new Error(
            "You're sending messages too quickly. Please wait a moment before trying again."
          )
      );
    }

    if (error.status === 503) {
      const errorMessage =
        error.error?.error_details?.includes('quota') ||
        error.error?.message?.includes('quota')
          ? 'AI assistant is temporarily unavailable due to quota limits. Please try again later.'
          : 'The AI assistant is temporarily unavailable. Please try again later.';

      return throwError(() => new Error(errorMessage));
    }

    if (error.status === 400) {
      const errorMessage =
        error.error?.error ||
        'Invalid message format. Please try rephrasing your question.';
      return throwError(() => new Error(errorMessage));
    }

    if (
      this.retryCount < this.maxRetries &&
      (error.status >= 500 || error.status === 0)
    ) {
      this.retryCount++;

      return timer(1000 * this.retryCount).pipe(
        switchMap(() => this.sendMessage(originalMessage, conversationHistory))
      );
    }

    const fallbackResponse: ChatResponse = {
      message:
        "I apologize, but I'm experiencing technical difficulties. You can still use the regular search function or try asking me again in a moment.",
      intent: 'error',
      timestamp: new Date().toISOString(),
      error: true,
      success: false,
    };

    return of(fallbackResponse);
  }

  private redirectToLogin(): void {
    this.router.navigate(['/login'], {
      queryParams: {
        returnUrl: this.router.url,
        feature: 'ai-chat',
        message: 'Please log in to use the AI chat assistant',
      },
    });
  }

  checkHealth(): Observable<HealthResponse> {
    return this.http.get<HealthResponse>(`${this.apiUrl}/chat/health`).pipe(
      catchError((error) => {
        return of({
          status: 'unhealthy',
          ai_service: 'unavailable',
          timestamp: new Date().toISOString(),
          error: 'Service unavailable',
        } as HealthResponse);
      })
    );
  }

  getCompanyInfo(): Observable<CompanyInfo> {
    return this.http
      .get<{ company_info: CompanyInfo; timestamp: string; success: boolean }>(
        `${this.apiUrl}/chat/company-info`
      )
      .pipe(
        map((response) => {
          if (!response.success || !response.company_info) {
            throw new Error('Invalid company info response');
          }
          return response.company_info;
        }),
        catchError((error) => {
          return throwError(
            () => new Error('Failed to load company information')
          );
        })
      );
  }

  streamMessage(
    message: string,
    conversationHistory: Array<{ role: string; content: string }> = []
  ): Observable<{ type: string; content?: string; timestamp: string }> {
    if (
      !message ||
      typeof message !== 'string' ||
      message.trim().length === 0
    ) {
      return throwError(() => new Error('Message cannot be empty'));
    }

    const payload = {
      message: message.trim(),
      conversation_history:
        this.validateConversationHistory(conversationHistory),
    };

    return new Observable((observer) => {
      this.isThinkingSubject.next(true);

      const eventSource = new EventSource(`${this.apiUrl}/chat/stream`);

      this.http
        .post(`${this.apiUrl}/chat/stream`, payload, {
          headers: this.getHeaders(),
          responseType: 'text',
        })
        .subscribe({
          error: (error) => {
            this.isThinkingSubject.next(false);
            observer.error(error);
          },
        });

      eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          observer.next(data);

          if (data.type === 'end' || data.type === 'error') {
            eventSource.close();
            this.isThinkingSubject.next(false);
            observer.complete();
          }
        } catch (e) {
          eventSource.close();
          this.isThinkingSubject.next(false);
          observer.error(new Error('Failed to parse streaming data'));
        }
      };

      eventSource.onerror = (error) => {
        eventSource.close();
        this.isThinkingSubject.next(false);
        observer.error(new Error('Stream connection failed'));
      };

      return () => {
        eventSource.close();
        this.isThinkingSubject.next(false);
      };
    });
  }

  private trackChatInteraction(message: string, intent: string): void {
    try {
      if (typeof (window as any).gtag !== 'undefined') {
        (window as any).gtag('event', 'ai_chat_interaction', {
          event_category: 'AI Chat',
          event_label: intent,
          custom_parameter_1: message.length,
        });
      }
    } catch (error) {}
  }

  isCurrentlyThinking(): boolean {
    return this.isThinkingSubject.value;
  }

  isServiceOnline(): boolean {
    return this.isOnlineSubject.value;
  }

  clearChatHistory(): void {
    try {
      localStorage.removeItem('markado-ai-chat-history');
    } catch (error) {}
  }

  exportChatHistory(): ChatMessage[] | null {
    try {
      const saved = localStorage.getItem('markado-ai-chat-history');
      return saved ? JSON.parse(saved) : null;
    } catch (error) {
      return null;
    }
  }
}
